import NoRecords from './NoRecords';
import React from 'react';

/**
 * A title + sentence card with no evidence — the honest rendering for a panel this
 * release cannot draw (§2.4.9) and for every `insufficient`-confidence panel (§G.2).
 *
 * Lifted to `shared/` from `search-console/GenericCard.js` (which wrapped the
 * feature-local `PanelCard`) so both the Search Console V6 build and the
 * Conversation V6 build share one primitive. It is deliberately SELF-CONTAINED —
 * it does not depend on `search-console/PanelCard` — because a shared component
 * must not reach back into a feature folder. The old path re-exports this, so
 * existing `search-console/GenericCard` imports keep working.
 *
 * Renders the house card shell: an uppercase title strip identifying the panel, over a
 * CENTRED empty-state — a muted mark and the verbatim sentence, balanced in the middle of
 * the card rather than stranded top-left with a tall blank beneath. This card carries no
 * chart, no numbers and no reserved evidence space (§G.2); when it sits in a stretched
 * pair its whole point is that there is nothing to draw, so the message owns the middle
 * instead of leaving the panel looking half-loaded. No chevron — there is nothing to
 * disclose.
 *
 * Accepts either a `panel` object (`{ title, sentence, emphasis }`) or explicit
 * `title` / `sentence` props; the explicit props win when both are given, so a
 * caller that holds the two strings loose does not have to synthesise a panel.
 *
 * @param {object} [props.panel] a panel object carrying `title` / `sentence`
 * @param {string} [props.title] title override
 * @param {string} [props.sentence] sentence override
 * @param {boolean} [props.showEmptyMark=true] draw the centred "No data" mark under the
 *   sentence. A caller whose sentence already explains the empty state (Search Console)
 *   turns it off — the mark only repeated what the sentence said.
 */
const GenericCard = ({ panel, title, sentence, showEmptyMark = true }) => {
  const resolvedTitle = title ?? panel?.title;
  const resolvedSentence = sentence ?? panel?.sentence;
  const notable = panel?.emphasis === 'notable';

  return (
    <section
      id={panel?.panel ? `panel-${panel.panel}` : undefined}
      data-panel={panel?.panel}
      data-generic-card="true"
      tabIndex={panel?.panel ? -1 : undefined}
      className={`scroll-mt-24 flex flex-col min-h-[240px] bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
        notable ? 'border-l-4 border-l-[#32B1FF]' : ''
      }`}
    >
      {resolvedTitle && (
        <div className="px-5 pt-3 flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {resolvedTitle}
          </h3>
        </div>
      )}
      {/* Title then muted description, tight — the same shape every other panel uses (the
          description sits right under the title, not floating in the card's centre or
          pushed down by a rule) — with the hairline rule AFTER the summary, exactly where
          `PanelCard` puts it. §2.4.8 — the sentence is rendered verbatim; the front end
          writes none of its own. Absent sentence means no line at all, never a placeholder. */}
      {resolvedSentence && (
        <p
          data-testid="panel-sentence"
          className="px-5 pt-1 pb-4 border-b border-gray-100 dark:border-gray-800 font-normal leading-normal text-gray-500 dark:text-gray-400 text-xs"
        >
          {resolvedSentence}
        </p>
      )}
      {/* A muted empty-state mark fills the body, so a card with nothing to draw reads as a
          deliberate "no data" state rather than a panel that failed to load. Purely
          decorative — the sentence above carries the meaning and the front end writes no
          copy of its own (§2.4.8) — so it is `aria-hidden`. */}
      {showEmptyMark && (
        <div className="flex-1 flex" data-testid="generic-empty-state" aria-hidden="true">
          <NoRecords className="flex-1" />
        </div>
      )}
    </section>
  );
};

export default GenericCard;
